import type { ClientStatus } from '@frontend/api-types';

export const evaluateClientQuota = (clientStatus: ClientStatus) => {
  const { currentActiveTasks, individualMaxActiveTasks, planMaxActiveTasks } = clientStatus;

  const exceedsPlanButWithIndividualLimit =
    currentActiveTasks !== null &&
    planMaxActiveTasks !== null &&
    individualMaxActiveTasks !== null &&
    currentActiveTasks >= planMaxActiveTasks &&
    currentActiveTasks < individualMaxActiveTasks;

  const exceedsPlanWithoutIndividualLimit =
    currentActiveTasks !== null &&
    planMaxActiveTasks !== null &&
    currentActiveTasks >= planMaxActiveTasks &&
    individualMaxActiveTasks === null;

  // This is the case where the user has exceeded both the plan limit and the individual limit
  //  is expected to be higher than the plan limit
  const exceedsBothPlanAndIndividualLimit =
    currentActiveTasks !== null &&
    individualMaxActiveTasks !== null &&
    planMaxActiveTasks !== null &&
    currentActiveTasks >= individualMaxActiveTasks &&
    individualMaxActiveTasks >= planMaxActiveTasks;

  const remainingTasks = () => {
    if (individualMaxActiveTasks && currentActiveTasks) {
      return individualMaxActiveTasks - currentActiveTasks;
    } else if (planMaxActiveTasks && currentActiveTasks) {
      return planMaxActiveTasks - currentActiveTasks;
    } else {
      return 0;
    }
  };

  return {
    exceedsBothPlanAndIndividualLimit,
    exceedsPlanButWithIndividualLimit,
    exceedsPlanWithoutIndividualLimit,
    isThrottled: exceedsBothPlanAndIndividualLimit || exceedsPlanWithoutIndividualLimit,
    remainingTasks: remainingTasks(),
  };
};

export const skipQuotaStep = (clientStatus: ClientStatus, hideIndividualTaskQuotaStep: boolean) => {
  const quota = evaluateClientQuota(clientStatus);
  if (
    !quota.exceedsPlanWithoutIndividualLimit &&
    !quota.exceedsPlanButWithIndividualLimit &&
    !quota.exceedsBothPlanAndIndividualLimit
  ) {
    return true;
  } else if (quota.exceedsPlanButWithIndividualLimit && hideIndividualTaskQuotaStep) {
    return true;
  } else {
    return false;
  }
};

export const getQuotaContent = (clientStatus: ClientStatus) => {
  const quota = evaluateClientQuota(clientStatus);

  if (quota.exceedsPlanButWithIndividualLimit) {
    return {
      description:
        "In the future, we may decide to reduce you to your plan's default limit, but will give you a heads up first!",
      title: `We've increased your maximum number of active tasks to ${clientStatus.individualMaxActiveTasks}, although your plan only allows ${clientStatus.planMaxActiveTasks} active tasks at once.`,
      titleHighlight: clientStatus.individualMaxActiveTasks ? `${clientStatus.individualMaxActiveTasks},` : '',
    };
  } else if (quota.exceedsPlanWithoutIndividualLimit) {
    return {
      description: 'We’ll start working on it after you complete another task.',
      title: `While Duckbill tasks are unlimited, you can only have up to ${clientStatus.planMaxActiveTasks} tasks open at any time! You're currently at this point, so we can't get started on this task until you close out an open one. This task will be safe in your draft folder in the meantime!`,
      titleHighlight: clientStatus.planMaxActiveTasks ? `${clientStatus.planMaxActiveTasks}` : '',
    };
  } else if (quota.exceedsBothPlanAndIndividualLimit) {
    return {
      description:
        'We’ll start working on it after you complete another task. In the future, we may reduce your limit, but will give you a heads up first!',
      title: `While Duckbill tasks are unlimited, you can only have up to ${clientStatus.planMaxActiveTasks} tasks open at any time! You're currently at this point, so we can't get started on this task until you close out an open one. This task will be safe in your draft folder in the meantime!`,
      titleHighlight: clientStatus.planMaxActiveTasks ? `${clientStatus.planMaxActiveTasks}` : '',
    };
  } else {
    return { description: '', title: '', titleHighlight: '' };
  }
};
